import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToShortGuestReservations } from "../../../../redux/shortstayReservationSlice";
import { fetchAllUsers } from "../../../../redux/authSlice";
import { listenToShortstayData } from "../../../../redux/shortstaysSlice";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import CancelIcon from "@mui/icons-material/Cancel";
import ReplayIcon from "@mui/icons-material/Replay"; // Rebook icon
import { CircularProgress } from "@mui/material";

const GuestCancelledBookings: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const reservations = useSelector(
    (state: RootState) => state.shortstayReservations.guestReservations
  );
  const allUsers = useSelector((state: RootState) => state.auth.allUsers);
  const listings = useSelector((state: RootState) => state.shortstays.listings);
  const loading = useSelector((state: RootState) => state.shortstayReservations.loading);

  const [expanded, setExpanded] = useState<string | null>(null);
  const [fetchedListings, setFetchedListings] = useState<string[]>([]);

  useEffect(() => {
    if (userId) {
      dispatch(listenToShortGuestReservations(userId));
      dispatch(fetchAllUsers());
    }
  }, [userId, dispatch]);

  useEffect(() => {
    reservations.forEach((res) => {
      if (!fetchedListings.includes(res.listingId)) {
        dispatch(listenToShortstayData(res.listingId));
        setFetchedListings((prev) => [...prev, res.listingId]);
      }
    });
  }, [reservations, fetchedListings, dispatch]);

  const cancelledReservations = reservations.filter((r) => r.isCancelled);

  const getHostProfile = (hostId: string) =>
    allUsers.find((u) => u.userId === hostId);

  const getListingPhoto = (listingId: string) => {
    const listing = listings.find((l) => l.listingId === listingId);
    return listing?.photos?.[0] || "/default-shortstay.jpg";
  };

  const getListingTitle = (listingId: string) => {
    const listing = listings.find((l) => l.listingId === listingId);
    return listing?.title || "Unknown Stay";
  };

  const getListingAddress = (listingId: string) => {
    const listing = listings.find((l) => l.listingId === listingId);
    const addr = listing?.address;
    if (!addr) return "";
    return [addr.street, addr.city, addr.country].filter(Boolean).join(", ");
  };

  const handleRebook = (listingId: string) => {
    navigate(`/shortdetails/${listingId}`);
  };

  const handleToggleExpand = (id: string) => {
    setExpanded((prev) => (prev === id ? null : id));
  };

  if (loading)
    return (
      <div style={{ textAlign: "center", marginTop: "100px" }}>
        <CircularProgress />
      </div>
    );

  return (
    <div style={styles.gridContainer}>
      {cancelledReservations.length === 0 ? (
        <p style={{ textAlign: "center", opacity: 0.6 }}>
          You haven’t cancelled any bookings yet.
        </p>
      ) : (
        cancelledReservations.map((res) => {
          const host = getHostProfile(res.hostId);
          const listingPhoto = getListingPhoto(res.listingId);
          const listingTitle = getListingTitle(res.listingId);

          const hostInitials =
            host?.firstName?.[0]?.toUpperCase() || host?.name?.[0]?.toUpperCase() || "H";

          return (
            <div key={res.id} style={styles.card}>
              <div style={styles.cardHeader} onClick={() => handleToggleExpand(res.id)}>
                <img src={listingPhoto} style={styles.thumbnail} alt="stay" />

                <div style={styles.headerInfo}>
                  <div>
                    <h3 style={styles.listingTitle}>{listingTitle}</h3>
                    <p style={styles.locationText}>{getListingAddress(res.listingId)}</p>
                  </div>

                  <div style={styles.dateSection}>
                    <p>
                      <strong>Check-In:</strong>{" "}
                      {new Date(res.checkIn).toLocaleDateString()}
                    </p>
                    <p>
                      <strong>Check-Out:</strong>{" "}
                      {new Date(res.checkOut).toLocaleDateString()}
                    </p>

                    <div style={styles.cancelStatus}>
                      <CancelIcon style={{ color: "#d32f2f", fontSize: 18 }} />
                      <span style={styles.cancelLabel}>Cancelled</span>
                    </div>
                  </div>
                </div>

                <div style={styles.chevron}>
                  {expanded === res.id ? <ChevronUp /> : <ChevronDown />}
                </div>
              </div>

              {expanded === res.id && (
                <div style={styles.expand}>
                  <p>
                    <strong>Total Paid:</strong> €{res.totalPaid?.toFixed(2)}
                  </p>
                  <p>
                    <strong>Refunded:</strong>{" "}
                    {res.isRefunded ? "Yes" : "No"}
                  </p>
                  <p>
  <strong>Refund Amount:</strong>{" "}
  {res.refundAmount ? `€${res.refundAmount.toFixed(2)}` : "€0.00"}
</p>

                  <p>
                    <strong>Host:</strong>{" "}
                    {host?.firstName || "Host"} {host?.lastName || ""}
                  </p>
               <p style={{ wordBreak: "break-all", maxWidth: "100%", overflowWrap: "break-word" }}>
  <strong>Reservation ID:</strong> {res.id}
</p>


                  <button
                    style={styles.rebookBtn}
                    onClick={() => handleRebook(res.listingId)}
                  >
                    <ReplayIcon style={{ marginRight: 6 }} />
                    Rebook Stay
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default GuestCancelledBookings;

const styles: { [key: string]: React.CSSProperties } = {
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "20px",
    padding: "20px",
    justifyContent: "center",
    alignItems: "start",
  },
  card: {
    backgroundColor: "rgba(255,255,255,0.85)",
    backdropFilter: "blur(8px)",
    borderRadius: "16px",
    boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    cursor: "pointer",
    width: "100%",
    maxWidth: "600px",
    margin: "0 auto",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
  },
  thumbnail: {
    width: "90px",
    height: "90px",
    borderRadius: "12px",
    objectFit: "cover",
    marginRight: "16px",
    boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
  },
  headerInfo: {
    flex: 1,
    display: "flex",
    justifyContent: "space-between",
  },
  listingTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#111",
    marginBottom: 4,
  },
  locationText: {
    fontSize: "0.85rem",
    color: "#555",
  },
  dateSection: {
    fontSize: "13px",
    textAlign: "right",
    color: "#333",
  },
  cancelStatus: {
    marginTop: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "4px",
  },
  cancelLabel: {
    fontSize: "12px",
    color: "#d32f2f",
    fontWeight: 600,
  },
  chevron: {
    marginLeft: "12px",
    opacity: 0.7,
    display: "flex",
    alignItems: "center",
  },
  expand: {
    padding: "12px 18px",
    borderTop: "1px solid rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.9)",
    borderBottomLeftRadius: "16px",
    borderBottomRightRadius: "16px",
    fontSize: "14px",
    color: "#222",
  },
  rebookBtn: {
    marginTop: "12px",
    background: "linear-gradient(145deg, #bf9b30, #111)",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    padding: "10px 18px",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    cursor: "pointer",
    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  },
};



/*import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../redux/store";

// Import the thunk that fetches all refunds for current guest in one go
import { fetchGuestRefundsForCurrentUser, Refund } from "../../../redux/shortRefundsSlice";

import { listenToShortGuestReservations } from "../../../redux/shortstayReservationSlice";
import { fetchAllUsers } from "../../../redux/authSlice";
import { listenToShortstayData } from "../../../redux/shortstaysSlice";

import PaymentIcon from "@mui/icons-material/Payment";
import RefundIcon from "@mui/icons-material/Receipt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import GroupIcon from "@mui/icons-material/Group";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/Info";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const GuestCancelledBookings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { guestId } = useParams<{ guestId: string }>();

  const guestReservations = useSelector(
    (state: RootState) => state.shortstayReservations.guestReservations
  );

  const refunds = useSelector((state: RootState) => state.shortRefunds.refunds);

  const loadingReservations = useSelector(
    (state: RootState) => state.shortstayReservations.loading
  );
  const loadingRefunds = useSelector(
    (state: RootState) => state.shortRefunds.status === "loading"
  );
  const listings = useSelector((state: RootState) => state.shortstays.listings);

  useEffect(() => {
    if (guestId) {
      dispatch(listenToShortGuestReservations(guestId));
      dispatch(fetchAllUsers());
      dispatch(fetchGuestRefundsForCurrentUser()); // <-- NO guestId here
    }
  }, [dispatch, guestId]);

  useEffect(() => {
    guestReservations.forEach((res) => {
      dispatch(listenToShortstayData(res.listingId));
    });
  }, [guestReservations, dispatch]);

  const cancelled = guestReservations.filter(
    (res) => res.reservationStatus?.toLowerCase() === "cancelled"
  );

  const handleListingClick = (listingId: string) => {
    navigate(`/shortdetails/${listingId}`);
  };

  if (loadingReservations || loadingRefunds) {
    return <p style={{ color: "#fff" }}>Loading cancelled bookings and refunds...</p>;
  }

  return (
    <div style={styles.wrapper}>
      <div style={styles.container}>
        <button
          style={styles.backBtn}
          onClick={() => navigate(`/shortbookings/${guestId}`)}
        >
          <ArrowBackIcon fontSize="small" /> Back to Bookings
        </button>

        <h2 style={styles.title}>Your Cancelled Bookings</h2>

        {cancelled.length === 0 ? (
          <p style={{ color: "#ccc" }}>You have no cancelled bookings.</p>
        ) : (
          cancelled.map((res) => {
            const listing = listings.find((l) => l.listingId === res.listingId);
            const img = listing?.photos?.[0] || "/fallback.jpg";

            const refund = refunds.find((r) => r.refundData.reservationId === res.id);

            return (
              <div key={res.id} style={styles.card}>
                <img
                  src={img}
                  alt="Listing"
                  style={styles.image}
                  onClick={() => handleListingClick(res.listingId)}
                />
                <div style={styles.details}>
                  <h4>{listing?.title || "Listing"}</h4>
                  <p>
                    {new Date(res.checkIn).toLocaleDateString()} →{" "}
                    {new Date(res.checkOut).toLocaleDateString()}
                  </p>
                  <p style={styles.status}>Status: Cancelled</p>

                  <hr style={{ borderColor: "#444", margin: "12px 0" }} />

                  {refund ? (
                    <div style={styles.refundContainer}>
  <div style={styles.refundItem}>
    <PaymentIcon fontSize="small" style={styles.icon} />
    Total Paid: <strong>{refund.refundData.totalPaid ?? "N/A"}</strong>
  </div>
  <div style={styles.refundItem}>
    <RefundIcon fontSize="small" style={styles.icon} />
    Refund Status: <strong>{refund.refundData.refundStatus ?? "N/A"}</strong>
  </div>
  <div style={styles.refundItem}>
    <AccountBalanceIcon fontSize="small" style={styles.icon} />
    Host Compensation: <strong>{refund.refundData.hostCompensation ?? "N/A"}</strong>
  </div>
  <div style={styles.refundItem}>
    <GroupIcon fontSize="small" style={styles.icon} />
    Guest Refunds: <strong>{refund.refundData.guestRefunds ?? "N/A"}</strong>
  </div>
  <div style={styles.refundItem}>
    <GroupIcon fontSize="small" style={styles.icon} />
    Guest Compensation: <strong>{refund.refundData.guestCompensation ?? "N/A"}</strong>
  </div>
  <div style={styles.refundItem}>
    <CancelIcon fontSize="small" style={styles.icon} />
    Cancellation Type: <strong>{refund.refundData.cancellationType ?? "N/A"}</strong>
  </div>
  <div style={styles.refundItem}>
    <InfoIcon fontSize="small" style={styles.icon} />
    Reason: <strong>{refund.refundData.cancellationReason ?? "N/A"}</strong>
  </div>
</div>

                  ) : (
                    <p style={{ color: "#888", fontStyle: "italic" }}>
                      No refund details available
                    </p>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default GuestCancelledBookings;

const styles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    backgroundColor: "#000",
    color: "#fff",
    minHeight: "100vh",
    padding: "80px 20px",
    display: "flex",
    justifyContent: "center",
  },
  container: {
    width: "100%",
    maxWidth: 700,
  },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 16,
    padding: "8px 12px",
    backgroundColor: "#bf9b30",
    color: "#000",
    border: "none",
    borderRadius: 30,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14,
  },
  title: {
    textAlign: "center",
    fontSize: "1.8rem",
    marginBottom: 20,
  },
  card: {
    backgroundColor: "#111",
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
    gap: 16,
    border: "1px solid #333",
  },
  image: {
    width: 80,
    height: 80,
    borderRadius: 8,
    objectFit: "cover",
    cursor: "pointer",
  },
  details: {
    flex: 1,
  },
  status: {
    color: "#ff4444",
    fontWeight: 600,
  },
  refundContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 8,
    marginTop: 8,
    color: "#ddd",
    fontSize: 14,
  },
  refundItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  icon: {
    color: "#bf9b30",
  },
};
/* End of commented code */